import type { PartnershipRecord } from "@itaas/insurance-contract";
import {
  decodePartnershipApprovalCode,
  encodePartnershipRequest,
  insurerCodeToOrgId,
  partnershipAcceptUrl,
  type PartnershipRequestPayload,
} from "@itaas/insurance-contract";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { getTenantDisplayName } from "@/core/tenant-display";
import { IAM_TENANT_SLUG } from "@/core/config";
import { getInsurerById } from "./insurer-store";

const PARTNERSHIPS_KEY = "hospitol.insurancePartnerships";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writePartnerships(list: HospitalPartnership[]): void {
  setStorageItem(PARTNERSHIPS_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:insurance-partnerships"));
}

export interface HospitalPartnership extends PartnershipRecord {
  insurerId: string;
  approvalCode: string;
}

function tenantContext(): {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  contactEmail: string;
} {
  const session = loadStoredSession();
  return {
    tenantId: session?.tenant?.id ?? `tenant-${IAM_TENANT_SLUG}`,
    tenantSlug: session?.tenant?.slug ?? IAM_TENANT_SLUG,
    tenantName: getTenantDisplayName(),
    contactEmail: session?.user?.email ?? "admin@hospital.demo",
  };
}

export function listHospitalPartnerships(filter?: {
  insurerId?: string;
  status?: PartnershipRecord["status"];
}): HospitalPartnership[] {
  let list = readJson<HospitalPartnership[]>(PARTNERSHIPS_KEY, []);
  if (filter?.insurerId) list = list.filter((p) => p.insurerId === filter.insurerId);
  if (filter?.status) list = list.filter((p) => p.status === filter.status);
  return list.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function getActivePartnershipForInsurer(insurerId: string): HospitalPartnership | null {
  return (
    listHospitalPartnerships({ insurerId, status: "active" }).find(
      (p) => p.status === "active",
    ) ?? null
  );
}

export function revokeHospitalPartnership(partnershipId: string): HospitalPartnership | null {
  const list = readJson<HospitalPartnership[]>(PARTNERSHIPS_KEY, []);
  const index = list.findIndex((p) => p.id === partnershipId);
  if (index < 0) return null;
  if (list[index]!.status !== "active") return list[index]!;

  list[index] = {
    ...list[index]!,
    status: "revoked",
    approvedAt: null,
  };
  writePartnerships(list);
  return list[index]!;
}

export function generatePartnershipRequest(insurerId: string): {
  requestKey: string;
  acceptUrl: string;
  payload: PartnershipRequestPayload;
} | null {
  const insurer = getInsurerById(insurerId);
  if (!insurer) return null;

  const ctx = tenantContext();
  const now = new Date();
  const expires = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const payload: PartnershipRequestPayload = {
    requestId: `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    hospitalTenantId: ctx.tenantId,
    hospitalTenantSlug: ctx.tenantSlug,
    hospitalName: ctx.tenantName,
    hospitalContactEmail: ctx.contactEmail,
    issuedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
  };
  const hubUrl = defaultInsuranceHubUrl();
  const requestKey = encodePartnershipRequest(payload);
  return {
    requestKey,
    acceptUrl: partnershipAcceptUrl(requestKey, hubUrl),
    payload,
  };
}

export function activatePartnershipWithCode(
  insurerId: string,
  approvalCode: string,
): HospitalPartnership | null {
  const decoded = decodePartnershipApprovalCode(approvalCode);
  if (!decoded) return null;

  const insurer = getInsurerById(insurerId);
  if (!insurer) return null;

  const ctx = tenantContext();
  const now = new Date().toISOString();
  const partnership: HospitalPartnership = {
    id: decoded.partnershipId,
    insurerOrgId: insurerCodeToOrgId(insurer.code),
    insurerId,
    hospitalTenantId: ctx.tenantId,
    hospitalTenantSlug: ctx.tenantSlug,
    hospitalName: ctx.tenantName,
    status: "active",
    requestId: "",
    approvedAt: now,
    createdAt: now,
    approvalCode: approvalCode.trim(),
  };

  const list = readJson<HospitalPartnership[]>(PARTNERSHIPS_KEY, []).filter(
    (p) => !(p.insurerId === insurerId && p.status === "active"),
  );
  list.unshift(partnership);
  writePartnerships(list);
  return partnership;
}

export function defaultInsuranceHubUrl(): string {
  return import.meta.env.VITE_INSURANCE_HUB_URL ?? "http://localhost:3119";
}

export function defaultInsuranceHubApiUrl(): string {
  return import.meta.env.VITE_INSURANCE_HUB_API_URL ?? "http://localhost:3120/api/v1";
}
